import { EventAggregator, inject, observable, resolve } from 'aurelia';
import { IRouter, IRouteViewModel, Params, route, RouteNode } from '@aurelia/router-lite';
import { IStore } from '@aurelia/state';

import { apiEndpoint, getAWSBucketEndpoint, websiteShortCode, environment } from '../../../environment';
import { DEVICES } from '../../../resources/constants';
import { BlogService } from '../../../services/blog-service';
import { WebsiteService } from '../../../services/website-service';
import { ActionNames, UpdateMetaDescription, UpdateMetaTitle, UpdateOgType } from '../../../state';
import { Helper } from '../../../resources/extensions';
import { SessionService } from '../../../services/session-service';

import './blogpost.scss';
import { NameFromShortcodeValueConverter } from '../../../resources/value-converters';

@route({
    path: ['blog/:blog'],
    title: 'Blogs',
})
@inject(IRouter, BlogService, WebsiteService, EventAggregator, IStore, Helper, NameFromShortcodeValueConverter, SessionService)
export class Blogpost implements IRouteViewModel {
    baseAwsEndpoint: string;
    blogs;
    blog;
    blogTag;
    count = 1;
    environment = apiEndpoint();
    relatedArticles;
    blogsCount: number;
    arrowTriggered;
    arrowRotated: boolean;
    lastEventTriggered: string;
    blogRoute: string;
    isAttached = false;
    @observable page: number = 1;
    perPage: number;
    pageLoading: boolean = true;
    breadcrumbs: { title: string; href: string; tooltip?: boolean }[] = [];
    homePageRoute;
    readingTimeResult = 0;
    sizeChanged;
    width;
    params;
    currentRouteNode;
    DEVICES;
    scrollView: any;
    skeletonPostContent: any[] = Array.from({ length: 4 });
    shortCode: string = websiteShortCode() ?? 'CG';
    blogPostCategory: string = 'all';
    blogpostContainer: HTMLElement;
    private readonly requiredWebComponents = [
        'cds-skeleton',
        'cds-breadcrumbs',
        'cds-breadcrumb',
        'cds-icon',
        'cds-pagination',
    ];

    constructor(
        private router: IRouter,
        private blogService: BlogService,
        private websiteService: WebsiteService,
        private eventAggregator: EventAggregator,
        private store: IStore<{}, UpdateMetaTitle | UpdateMetaDescription | UpdateOgType> = resolve(IStore),
        private helper: Helper,
        private nameFromShortcodeValueConverter: NameFromShortcodeValueConverter,
        private sessionService: SessionService
    ) {
        this.baseAwsEndpoint = getAWSBucketEndpoint('blog') as string;
        this.DEVICES = DEVICES;
        this.width = document.body.clientWidth;
    }

    loading(params: Params, current: RouteNode) {
        this.params = params;
        this.currentRouteNode = current;
        this.scrollToTop();
    }

    private waitForWebComponentsReady() {
        if (typeof window === 'undefined' || !window.customElements) {
            return Promise.resolve();
        }

        return Promise.all(this.requiredWebComponents.map((tagName) => window.customElements.whenDefined(tagName)));
    }

    async attached() {
        const [pages, language] = await Promise.all([
            this.websiteService.getPagesByWebsiteShortcode(),
            this.sessionService.getLanguage(),
            this.waitForWebComponentsReady(),
        ]);
        this.blogRoute = pages.find(x => x.name === 'Blog')?.routeName ?? 'blog';
        this.scrollToTop();
        this.isAttached = true;
        if (language && language.language.hrefLang !== 'en') {
            this.blog = await this.blogService.getBlogBySlugAndLanguage(this.params.blog, language.language.hrefLang);
        } else {
            this.blog = await this.blogService.getBlogBySlug(this.params.blog);
        }
        if (!this.blog || (window.location.hostname.includes('resources') && !this.blog?.blogTags?.some(x => x?.blogTag?.name?.toLowerCase() === 'csr'))) {
            this.router.load('404');
            return;
        }
        const regex = /[<][p][>][<][b][r][>][<][/][p][>]/g;
        this.blog.content = this.blog.content?.replace(regex, '');
        const componentTagRegex = /&lt;([A-Z0-9]+) component&gt;/g;
        this.blog.content = this.blog.content?.replace(componentTagRegex, (_match: string, code: string) => {
            return `<cds-price-button code="${code}"></cds-price-button>`;
        });

        const blogTitle = `${this.blog.title} - ${this.nameFromShortcodeValueConverter.toView(this.shortCode)}`;
        this.currentRouteNode.title = blogTitle;
        this.store.dispatch({ type: ActionNames.UPDATE_META_TITLE, value: blogTitle });
        this.store.dispatch({ type: ActionNames.UPDATE_META_DESCRIPTION, value: this.blog.metaDescription });
        this.store.dispatch({ type: ActionNames.UPDATE_OG_TYPE, value: 'article' });
        this.readingTimeResult = this.readingTime();
        const categoryTag = this.blog.blogTag?.name || this.blog.blogTags?.[0]?.blogTag?.name || 'All';

        this.breadcrumbs = [
            { title: 'Blogs', href: '' },
            { title: categoryTag, href: '' },
            { title: this.blog.title, href: `${this.blog.slug}`, tooltip: this.blog.title.length > 80 }
        ];
        this.handleReviewSchema();
        this.handleEventSubscriptions();
        await this.pageChanged();
        this.pageLoading = false;
    }

    navigateBack(event: Event) {
        event.preventDefault();
        const route = this.helper.languageRoute('');
        void this.router.load(route ? `/${route}` : '/');
    }

    navigateToAuthor(alias: string, event: Event) {
        event.preventDefault();
        const route = this.helper.languageRoute(`author/${alias.toLowerCase()}`);
        if (!route) {
            return;
        }

        void this.router.load(route.startsWith('/') ? route : `/${route}`);
    }

    detaching() {
        this.getReviewSchema()?.remove();
        this.sizeChanged?.dispose();
    }

    handleEventSubscriptions() {
        this.sizeChanged = this.eventAggregator.subscribe('size-changed', async (payload: any) => {
            this.width = payload.width;
            await this.pageChanged();
        });
    }

    async pageChanged() {
        if (this.isAttached) {
            this.blogs = await this.blogService.filterBlogs(
                {
                    page: this.page,
                    pageSize: this.perPage = 5,
                    tag: await this.blog.blogTag === null ? this.blog.blogTags?.[0]?.blogTag?.name : this.blog.blogTag?.name,
                    websites: [this.shortCode]
                }
            );
            if (this.blogs && this.blogs.blogPosts) {
                const filtered = this.blogs.blogPosts.filter(blogPost => blogPost.id !== this.blog.id);
                this.relatedArticles = filtered.slice(0, 4);
                this.blogsCount = this.blogs.totalCount - 1;
            } else {
                this.relatedArticles = [];
                this.blogsCount = 0;
            }
            this.count = Math.ceil(this.blogsCount / 4);
            if (this.page > this.count && this.count > 0) {
                this.page = this.count;
            }
        }
    }

    breadcrumbClick(label: string) {
        const categoryTag = this.blog.blogTag?.name || this.blog.blogTags?.[0]?.blogTag?.name || 'All';

        if (label.toLowerCase() === categoryTag.toLowerCase()) {
            const href = this.blog.blogTag?.slug === null || this.blog.blogTag?.slug === undefined ? this.blog.blogTags?.[0]?.blogTag?.slug : this.blog.blogTag?.slug;
            localStorage.setItem('blogsSelectedCategory', encodeURIComponent(href));
        }
    }

    handleBreadcrumbClick(crumb: { title: string; href: string }, event: Event) {
        event.preventDefault();
        this.breadcrumbClick(crumb.title);

        if (!crumb.href) {
            const rootRoute = this.helper.languageRoute('');
            void this.router.load(rootRoute ? `/${rootRoute}` : '/');
            return;
        }

        const route = this.helper.languageRoute(crumb.href);
        if (!route) {
            return;
        }

        void this.router.load(route.startsWith('/') ? route : `/${route}`);
    }

    changeSelectHandler() {
        this.handleFocusOut();
    }

    handleFocusOut() {
        if (!this.arrowTriggered) return;
        this.arrowTriggered.style.transform = 'rotate(0deg)';
        this.arrowRotated = false;
        this.lastEventTriggered = 'focusOut';
        setTimeout(() => {
            this.lastEventTriggered = '';
        }, 200);
    }

    handleClick(e) {
        this.arrowTriggered = e.target.querySelector('.arrow-icon');
        this.arrowTriggered.style.transform = this.arrowRotated ? 'rotate(0deg)' : 'rotate(180deg)';
        this.arrowRotated = !this.arrowRotated;
    }

    handleReviewSchema() {
        this.getReviewSchema()?.remove();
        const reviewSchema = document.createElement('script');
        reviewSchema.setAttribute('id', 'article-schema');
        reviewSchema.type = 'application/id+json';
        reviewSchema.innerHTML = `
        {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "${this.blog.title}",`;

        if (this.blog.previewImagePath) {
            reviewSchema.innerHTML += `"image": "${this.baseAwsEndpoint}${this.blog.previewImagePath}",`;
        }

        reviewSchema.innerHTML += `
            "datePublished": "${this.blog.createdDate}",
            "dateModified": "${this.blog.updatedDate}",`;

        if (this.blog.blogAuthor) {
            reviewSchema.innerHTML += `,
                "author": {
                    "@type": "Person",
                    "name": "${this.blog.blogAuthor.firstName + ' ' + this.blog.blogAuthor.lastName}",
                    "url": "${window.location.href}"
                }`;
        }

        reviewSchema.innerHTML += '}';

        document.head.appendChild(reviewSchema);
    }

    getReviewSchema = () => document.getElementById('article-schema');

    async tagClickHandler(tag) {
        await this.router.load(`category/${encodeURIComponent(tag.slug)}`);
    }

    readingTime() {
        const wordCount = this.blog.content?.split(' ').length;
        const readingTimeMinutes = Math.ceil(wordCount / 250);
        return readingTimeMinutes;
    }

    scrollToTop() {
        const mainHostScroll = document.querySelector('#main-page-host .simplebar-content-wrapper') as HTMLElement | null;
        if (mainHostScroll) {
            mainHostScroll.scrollTo({ top: 0, behavior: 'auto' });
        }

        window.scrollTo({ top: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        window.document.getElementById('navbar')?.scrollIntoView({ block: 'start' });
    }

    scrollToBlogpost() {
        window.document.getElementById('blogpost')?.scrollIntoView();
    }

    handlePagination(value: CustomEvent): void {
        return this.page = value.detail.page;
    }

    get bannerImageByWebsite() {
        const isResources = window.location.hostname.includes('resources');
        const basePath = isResources || environment() === 'local' ? '' : '/blog';

        const logos = {
            CG: 'logo/logo-cg.svg',
            CX: 'logo/logo-cx.svg',
            DS: 'logo/ds-logo-dark.svg',
            GT: 'logo/gt-logo-dark.svg'
        };

        return logos[this.shortCode]
            ? `${basePath}/${logos[this.shortCode]}`
            : 'UNDEFINED';
    }
}
